import React from 'react';
// Minimal markdown renderer owned by <PlunkChat> (host-agnostic rule —
// AI_CHAT_UX §4/§Q7: the component carries its OWN renderer; the App's
// markdown.jsx known render bug is not inherited). Supports what the
// assistant actually emits: headings, bold/italic/code, links, bullet +
// numbered lists, tables, paragraphs. Streams safely — partial markdown
// renders as best-effort without throwing.

function inline(text, keyBase) {
  // Tokenize links, bold, italic, code within a text run.
  const out = [];
  let rest = text;
  let k = 0;
  const pattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*/;
  for (;;) {
    const m = rest.match(pattern);
    if (!m) {
      if (rest) out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const key = `${keyBase}-${k++}`;
    if (m[1] !== undefined) {
      const href = m[2];
      const external = /^https?:/i.test(href);
      out.push(/*#__PURE__*/React.createElement("a", {
        key: key,
        href: href,
        className: "pchat-link",
        target: external ? '_blank' : undefined,
        rel: external ? 'noreferrer' : undefined
      }, m[1]));
      // Bold/italic wrap other inline markup ("**[Report](/Reports/view/x)**" is how
      // the assistant links a created item) — render their contents recursively.
    } else if (m[3] !== undefined) out.push(/*#__PURE__*/React.createElement("strong", {
      key: key
    }, inline(m[3], key)));else if (m[4] !== undefined) out.push(/*#__PURE__*/React.createElement("code", {
      key: key,
      className: "pchat-code"
    }, m[4]));else if (m[5] !== undefined) out.push(/*#__PURE__*/React.createElement("em", {
      key: key
    }, inline(m[5], key)));
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

// Right-align cells that read as numbers (AI_CHAT_UX §4: numbers are the
// product — tabular-nums, right-aligned, currency formatted upstream).
const numeric = s => /^[\s$€£%+\-—–]*[\d,.]+[\s%MKB]*$/.test((s || '').trim()) && /\d/.test(s);
function table(lines, keyBase) {
  const rows = lines.map(l => l.replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
  const header = rows[0];
  const body = rows.slice(1).filter(r => !r.every(c => /^:?-{2,}:?$/.test(c)));
  return /*#__PURE__*/React.createElement("div", {
    key: keyBase,
    className: "pchat-tablewrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "pchat-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, header.map((c, i) => /*#__PURE__*/React.createElement("th", {
    key: i
  }, inline(c, `${keyBase}h${i}`))))), /*#__PURE__*/React.createElement("tbody", null, body.map((r, ri) => /*#__PURE__*/React.createElement("tr", {
    key: ri
  }, r.map((c, ci) => /*#__PURE__*/React.createElement("td", {
    key: ci,
    className: numeric(c) ? 'pchat-num' : undefined
  }, inline(c, `${keyBase}r${ri}c${ci}`))))))));
}
export default function Markdown({
  text
}) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    // table: consecutive lines containing |
    if (line.includes('|') && line.trim().startsWith('|')) {
      const tbl = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tbl.push(lines[i]);
        i++;
      }
      if (tbl.length >= 2) {
        blocks.push(table(tbl, `t${k++}`));
        continue;
      }
      blocks.push(/*#__PURE__*/React.createElement("p", {
        key: `p${k++}`,
        className: "pchat-p"
      }, inline(tbl.join(' '), `p${k}`)));
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) {
      const Tag = `h${Math.min(6, h[1].length + 3)}`;
      blocks.push(/*#__PURE__*/React.createElement(Tag, {
        key: `h${k++}`,
        className: "pchat-h"
      }, inline(h[2], `h${k}`)));
      i++;
      continue;
    }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(line)) {
      const items = [];
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
        i++;
      }
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(/*#__PURE__*/React.createElement(Tag, {
        key: `l${k++}`,
        className: "pchat-list"
      }, items.map((it, ii) => /*#__PURE__*/React.createElement("li", {
        key: ii
      }, inline(it, `l${k}i${ii}`)))));
      continue;
    }
    // paragraph: join consecutive plain lines
    const para = [];
    while (i < lines.length && lines[i].trim() && !lines[i].trim().startsWith('|') && !/^(#{1,4})\s+/.test(lines[i]) && !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(/*#__PURE__*/React.createElement("p", {
      key: `p${k++}`,
      className: "pchat-p"
    }, inline(para.join(' '), `p${k}`)));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, blocks);
}